<template>
  <div class="app-shell">
    <aside class="sidebar" :class="{ 'sidebar-open': isMobileMenuOpen }">
      <router-link to="/dashboard" class="sidebar__brand">
        <img class="sidebar__brand-image" :src="brandLogo" alt="ZP Track" />
      </router-link>

      <nav class="sidebar__nav">
        <router-link
          v-for="item in navigationItems"
          :key="item.to"
          :to="item.to"
          class="sidebar__link"
          active-class="sidebar__link--active"
          @click.native="closeMobileMenu"
        >
          <CIcon :name="item.icon" height="18" />
          <span>{{ item.label }}</span>
        </router-link>
      </nav>

      <div class="sidebar__footer">
        <div class="sidebar__footer-actions">
          <div class="sidebar__user">
            <CIcon name="cil-user" height="18" />
            <span>{{ userName }}</span>
          </div>
          <button class="sidebar__logout" type="button" @click="onLogoutClick">
            <CIcon name="cil-lock-locked" height="18" />
            <span>Logout</span>
          </button>
        </div>
        <div class="sidebar__version">Version: {{ appVersion }}</div>
      </div>
    </aside>

    <button
      v-if="isMobileMenuOpen"
      class="mobile-overlay"
      type="button"
      aria-label="Close menu"
      @click="closeMobileMenu"
    />

    <div class="content-shell">
      <header class="mobile-topbar">
        <router-link to="/dashboard" class="mobile-topbar__brand">
          <img class="sidebar__brand-image sidebar__brand-image--small" :src="brandLogo" alt="ZP Track" />
        </router-link>
        <button class="mobile-topbar__menu" type="button" aria-label="Open menu" @click="toggleMobileMenu">
          <CIcon name="cil-menu" height="26" />
        </button>
      </header>

      <main class="content-main">
        <transition name="fade">
          <div v-if="isDataLoaded" class="content-wrap">
            <section v-if="$route.name === 'Dashboard'" class="summary-card">
              <div class="summary-card__grid">
                <div class="summary-card__row summary-card__row--header">
                  <span>Period</span>
                  <span>Tezina</span>
                  <span>Kcal</span>
                  <span>San</span>
                </div>
                <div class="summary-card__row">
                  <span>Dan</span>
                  <span>{{ weightDay }}</span>
                  <span :class="calories.day < maxCalories ? 'text-success' : 'text-danger'">
                    {{ calories.day ? calories.day : "-" }}
                  </span>
                  <span :class="sleep.day > minSleep ? 'text-success' : 'text-danger'">
                    {{ sleep.day ? sleep.day.toFixed(1) : "-" }}
                  </span>
                </div>
                <div class="summary-card__row">
                  <span>Tjedan</span>
                  <span :class="weight.week.includes('(-') ? 'text-success' : 'text-danger'">
                    {{ weight.week }}
                  </span>
                  <span :class="calories.week < maxCalories ? 'text-success' : 'text-danger'">
                    {{ calories.week ? calories.week : "-" }}
                  </span>
                  <span :class="sleep.week > minSleep ? 'text-success' : 'text-danger'">
                    {{ sleep.week.toFixed(1) }}
                  </span>
                </div>
                <div class="summary-card__row">
                  <span>Mjesec</span>
                  <span :class="weight.month.includes('(-') ? 'text-success' : 'text-danger'">
                    {{ weight.month }}
                  </span>
                  <span :class="calories.month < maxCalories ? 'text-success' : 'text-danger'">
                    {{ calories.month ? calories.month : "-" }}
                  </span>
                  <span :class="sleep.month > minSleep ? 'text-success' : 'text-danger'">
                    {{ sleep.month.toFixed(1) }}
                  </span>
                </div>
              </div>
            </section>

            <div class="page-content">
              <router-view></router-view>
            </div>
          </div>
        </transition>
      </main>
    </div>
  </div>
</template>

<script>
import { services } from "../services/services";
import weightsUtils from "../../weightsUtilsES6";
import moment from "moment";
import brandLogo from "../assets/branding/zp-track-logo.png";

const getAverage = (values) => values.reduce((a, b) => a + b, 0) / values.filter((c) => c > 0).length || 0;
const todayDate = moment()
  .utcOffset(0, true)
  .toISOString()
  .substring(0, 10);
const yesterdayDate = moment()
  .utcOffset(0, true)
  .add("days", -1)
  .toISOString()
  .substring(0, 10);

export default {
  data: function () {
    return {
      userName: "",
      isDataLoaded: false,
      isMobileMenuOpen: false,
      appVersion: process.env.VUE_APP_VERSION || "dev",
      brandLogo,
      weight: {},
      calories: {},
      sleep: {},
      navigationItems: [
        { label: "Tezina", to: "/weight-history", icon: "cil-balance-scale" },
        { label: "Hrana", to: "/calories", icon: "cil-chart-pie" },
        { label: "San", to: "/sleep", icon: "cil-moon" },
        { label: "Unos tezine", to: "/add-weight", icon: "cil-plus" },
        { label: "Postavke", to: "/limits", icon: "cil-settings" },
      ],
    };
  },
  computed: {
    maxCalories() {
      return this.$store.state.maxCalories;
    },
    minSleep() {
      return this.$store.state.minSleep;
    },
    weightDay() {
      return weightsUtils.getTodayWeight(this.$store.state.weightHistory);
    },
  },
  methods: {
    onLogoutClick() {
      localStorage.removeItem("jwt");
      location.reload();
    },
    toggleMobileMenu() {
      this.isMobileMenuOpen = !this.isMobileMenuOpen;
    },
    closeMobileMenu() {
      this.isMobileMenuOpen = false;
    },
  },
  watch: {
    $route() {
      this.closeMobileMenu();
    },
  },
  mounted() {
    Promise.all([services.getCurrentUserWeights(), services.getCalories(), services.getSleep(), services.getLoggedInUser()].map((p) => p.catch((e) => e))).then((responses) => {
      this.$store.commit("initializeWeights", responses[0].body);
      this.$store.commit("calories", responses[1].body);
      this.$store.commit("sleep", responses[2].body);
      this.userName = responses[3].body.name;
      this.$store.commit("limits", responses[3].body.limits);
      this.$store.commit("withings", responses[3].body.withingsClientId);
      this.$store.commit("fitbit", responses[3].body.fitbitClientId);
      this.$store.commit("googleHealth", {
        configured: responses[3].body.googleHealthConfigured,
        connected: responses[3].body.googleHealthConnected,
      });
      this.isDataLoaded = true;

      this.weight.week = weightsUtils.getWeekWeight(this.$store.state.weightHistory);
      this.weight.month = weightsUtils.getMonthWeight(this.$store.state.weightHistory);

      this.calories.day = this.$store.state.calories.find((b) => b.date === yesterdayDate)?.value;
      let caloriesWeek = this.$store.state.calories
        .filter(
          (b) =>
            b.date < todayDate &&
            b.date >=
            moment()
              .add(-7, "days")
              .toISOString()
              .substring(0, 10)
        )
        .map((c) => c.value);
      this.calories.week = parseInt(getAverage(caloriesWeek));
      let caloriesMonth = this.$store.state.calories
        .filter(
          (b) =>
            b.date < todayDate &&
            b.date >=
            moment()
              .add(-1, "months")
              .toISOString()
              .substring(0, 10)
        )
        .map((c) => c.value);
      this.calories.month = parseInt(getAverage(caloriesMonth));

      this.sleep.day = this.$store.state.sleep.find((s) => s.date === todayDate)?.value;
      let sleepWeek = this.$store.state.sleep
        .filter(
          (b) =>
            b.date >=
            moment()
              .add(-6, "days")
              .toISOString()
              .substring(0, 10)
        )
        .map((c) => c.value);
      this.sleep.week = Math.round(getAverage(sleepWeek) * 10) / 10;
      let sleepMonth = this.$store.state.sleep
        .filter(
          (b) =>
            b.date < todayDate &&
            b.date >=
            moment()
              .add(-1, "months")
              .toISOString()
              .substring(0, 10)
        )
        .map((c) => c.value);
      this.sleep.month = getAverage(sleepMonth);
    });
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.app-shell {
  min-height: 100vh;
  background: #f7f8fa;
  color: #0d1b2a;
  overflow-x: hidden;
}

.sidebar {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 30;
  display: flex;
  flex-direction: column;
  width: 280px;
  height: 100vh;
  padding: 28px 18px 20px;
  background: #ffffff;
  border-right: 1px solid #d9dee8;
}

.sidebar__brand {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px 24px;
  border-bottom: 1px solid #edf1f5;
  color: inherit;
  text-decoration: none;
}

.sidebar__brand-image {
  display: block;
  width: min(100%, 120px);
  height: auto;
  object-fit: contain;
}

.sidebar__brand-image--small {
  width: 86px;
}

.sidebar__nav {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 26px 0;
}

.sidebar__link {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 15px 16px;
  border-radius: 14px;
  color: #526074;
  font-family: var(--font-display);
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  text-decoration: none;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.sidebar__link:hover,
.sidebar__link--active {
  background: #f1f3f7;
  color: #0d1b2a;
}

.sidebar__footer {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: auto;
  padding: 20px 8px 0;
  border-top: 1px solid #edf1f5;
}

.sidebar__footer-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.sidebar__user,
.sidebar__logout {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #526074;
  font-family: var(--font-display);
  font-size: 1rem;
  font-weight: 600;
}

.sidebar__logout {
  padding: 0.625rem 1rem;
  border: 1px solid transparent;
  border-radius: 0.375rem;
  text-align: left;
  color: #ffffff;
}

.sidebar__version {
  color: #8a94a6;
  font-family: var(--font-display);
  font-size: 0.9rem;
  font-weight: 500;
}

.content-shell {
  min-height: 100vh;
  margin-left: 280px;
  width: calc(100% - 280px);
  max-width: 100%;
}

.mobile-topbar {
  display: none;
}

.content-main {
  padding: 0;
}

.content-wrap {
  padding: 28px 32px 40px;
}

.summary-card {
  margin-bottom: 24px;
  background: #ffffff;
  border: 1px solid #d9dee8;
  border-radius: 24px;
  overflow: hidden;
}

.summary-card__grid {
  padding: 10px 24px 18px;
}

.summary-card__row {
  display: grid;
  grid-template-columns: max-content max-content max-content max-content;
  justify-content: space-between;
  align-items: start;
  padding: 2px 0;
  border-bottom: 1px solid #edf1f5;
}

.summary-card__row:last-child {
  border-bottom: 0;
}

.summary-card__row span {
  min-width: 0;
}

.summary-card__row:not(.summary-card__row--header) span:nth-child(1) {
  min-width: 88px;
}

.summary-card__row:not(.summary-card__row--header) span:nth-child(2) {
  min-width: 128px;
  padding-left: 10px;
  white-space: nowrap;
}

.summary-card__row:not(.summary-card__row--header) span:nth-child(3) {
  min-width: 64px;
  position: relative;
  left: -10px;
}

.summary-card__row:not(.summary-card__row--header) span:nth-child(4) {
  min-width: 34px;
  text-align: right;
  padding-right: 8px;
}

.summary-card__row--header {
  color: #6b778c;
  font-family: var(--font-display);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.page-content {
  background: transparent;
}

.mobile-overlay {
  position: fixed;
  inset: 0;
  z-index: 20;
  border: 0;
  background: rgba(13, 27, 42, 0.28);
}

@media (max-width: 991.98px) {
  .sidebar {
    transform: translateX(-100%);
    transition: transform 0.24s ease;
    box-shadow: 0 20px 48px rgba(13, 27, 42, 0.12);
  }

  .sidebar-open {
    transform: translateX(0);
  }

  .content-shell {
    margin-left: 0;
    width: 100%;
  }

  .mobile-topbar {
    position: sticky;
    top: 0;
    left: 0;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    z-index: 15;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    background: rgba(255, 255, 255, 0.96);
    border-bottom: 1px solid #d9dee8;
    backdrop-filter: blur(12px);
  }

.mobile-topbar__brand {
    display: flex;
    align-items: center;
    color: inherit;
    text-decoration: none;
  }

  .mobile-topbar__menu {
    display: grid;
    place-items: center;
    width: 42px;
    height: 42px;
    padding: 0;
    border: 0;
    border-radius: 12px;
    background: #f1f3f7;
    color: #526074;
  }

  .content-wrap {
    padding: 14px 0 24px;
  }

  .summary-card {
    margin: 0 10px 18px;
    border-left: 1px solid #d9dee8;
    border-right: 1px solid #d9dee8;
    border-radius: 14px;
  }

  .summary-card__grid {
    padding-left: 16px;
    padding-right: 16px;
  }

  .summary-card__row {
    grid-template-columns: max-content max-content max-content max-content;
    justify-content: space-between;
    align-items: start;
    font-size: 0.92rem;
  }

  .page-content {
    padding: 0 10px;
  }
}

@media (min-width: 992px) {
  .mobile-overlay {
    display: none;
  }
}
</style>
